/**
 * 분석 리포트 페이지 - v10.13
 * 기간별 통계 분석 (전체 IP / 외부 IP 비교)
 */
import React, { useEffect, useState } from 'react'
import {
  Box,
  Grid,
  GridItem,
  VStack,
  HStack,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Badge,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  Button,
  Divider,
  Tag,
  Wrap,
  WrapItem,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Tooltip,
  Icon,
  Textarea,
  IconButton,
  Collapse,
  useDisclosure,
} from '@chakra-ui/react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
} from 'recharts'
import { FiCalendar, FiUsers, FiTrendingUp, FiAlertCircle, FiHash, FiCheck, FiX, FiEdit2, FiSave, FiRefreshCw } from 'react-icons/fi'
import axios from 'axios'

interface ReportData {
  period: {
    startDate: string
    endDate: string
  }
  dataRange: {
    minDate: string
    maxDate: string
    totalInDb: number
  }
  dmInternalIps: string[]
  all: {
    totalQueries: number
    peakDay: { date: string; count: number }
    answerRate: number
    statusStats: Array<{ rag_status: string; count: number }>
    topKeywords: Array<{ keyword: string; count: number }>
    dailyTrend: Array<{ date: string; count: number }>
  }
  external: {
    totalQueries: number
    peakDay: { date: string; count: number }
    answerRate: number
    statusStats: Array<{ rag_status: string; count: number }>
    topKeywords: Array<{ keyword: string; count: number }>
    dailyTrend: Array<{ date: string; count: number }>
    errorLogs: Array<{
      id: number
      query: string
      response: string
      created_at: string
      ip: string
      status: string
      confidence: number
    }>
  }
}

// 기본 DM 내부 IP 목록
const DEFAULT_EXCLUDE_IPS = [
  '127.0.0.1',
  '118.47.140.18',
  '122.136.188.132',
  '210.120.27.131',
  '210.216.0.254',
  '211.40.240.159',
  '211.193.102.40',
  '118.235.82.161',
  '118.235.85.51',
  '118.235.84.247',
  '118.235.82.83',
  '110.13.4.62',
  '125.134.98.169',
  '210.103.83.39',
  '211.220.63.61'
]

const AnalysisReportPage: React.FC = () => {
  const [reportData, setReportData] = useState<ReportData | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  
  // 기본 기간: 12/15 ~ 1/14
  const [startDate, setStartDate] = useState('2025-12-15')
  const [endDate, setEndDate] = useState('2026-01-14')
  
  // 에러 로그 확장 상태 (질문 ID -> 확장 여부)
  const [expandedLogs, setExpandedLogs] = useState<Set<number>>(new Set())
  
  // 제외 IP 관련 상태
  const [excludeIps, setExcludeIps] = useState<string>(DEFAULT_EXCLUDE_IPS.join('\n'))
  const { isOpen: isIpEditorOpen, onToggle: onIpEditorToggle } = useDisclosure()

  const cardBg = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const highlightBg = useColorModeValue('blue.50', 'blue.900')

  // 제외 IP 목록 파싱 (줄바꿈, 쉼표, 공백 구분 지원)
  const parseExcludeIps = (): string[] => {
    return excludeIps
      .split(/[\n,\s]+/)
      .map(ip => ip.trim())
      .filter(ip => ip && /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(ip))
  }
  
  // 기본값으로 초기화
  const resetToDefaultIps = () => {
    setExcludeIps(DEFAULT_EXCLUDE_IPS.join('\n'))
  }
  
  // 데이터 로드
  const loadReport = async () => {
    try {
      setIsLoading(true)
      setError('')
      
      const ipsToExclude = parseExcludeIps()
      
      const response = await axios.get('/graph/analysis-report', {
        params: { 
          startDate, 
          endDate,
          excludeIps: ipsToExclude.join(',')  // 쉼표로 구분하여 전달
        }
      })
      
      if (response.data.success) {
        setReportData(response.data.data)
      } else {
        setError('데이터 로드 실패')
      }
    } catch (err: any) {
      console.error('분석 리포트 로드 실패:', err)
      setError(err.response?.data?.message || '분석 리포트를 불러오는데 실패했습니다.')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadReport()
  }, [])

  // 날짜 포맷
  const formatDate = (dateStr: string) => {
    if (!dateStr || dateStr === '-') return '-'
    const date = new Date(dateStr)
    return `${date.getMonth() + 1}월 ${date.getDate()}일`
  }

  // 통계 카드 컴포넌트
  const StatCard = ({ 
    label, 
    value, 
    subLabel, 
    color = 'blue',
    icon
  }: { 
    label: string
    value: string | number
    subLabel?: string
    color?: string
    icon?: React.ReactElement
  }) => (
    <Card bg={cardBg} border="1px" borderColor={borderColor} h="100%">
      <CardBody>
        <VStack align="start" spacing={2}>
          <HStack>
            {icon && <Box color={`${color}.500`}>{icon}</Box>}
            <Text fontSize="sm" color="gray.600" fontWeight="medium">{label}</Text>
          </HStack>
          <Text fontSize="3xl" fontWeight="bold" color={`${color}.600`}>
            {value}
          </Text>
          {subLabel && (
            <Text fontSize="xs" color="gray.500">{subLabel}</Text>
          )}
        </VStack>
      </CardBody>
    </Card>
  )

  // 키워드 Top 10 컴포넌트
  const KeywordRanking = ({ keywords }: { keywords: Array<{ keyword: string; count: number }> }) => (
    <VStack align="stretch" spacing={2}>
      {keywords.map((item, index) => (
        <HStack key={index} justify="space-between" p={2} bg={index < 3 ? highlightBg : 'transparent'} borderRadius="md">
          <HStack>
            <Badge colorScheme={index < 3 ? 'blue' : 'gray'} fontSize="sm" w="24px" textAlign="center">
              {index + 1}
            </Badge>
            <Text fontSize="sm" fontWeight={index < 3 ? 'bold' : 'normal'}>
              {item.keyword}
            </Text>
          </HStack>
          <Badge colorScheme="purple" variant="subtle">
            {item.count}회
          </Badge>
        </HStack>
      ))}
      {keywords.length === 0 && (
        <Text color="gray.500" textAlign="center" py={4}>키워드 데이터가 없습니다.</Text>
      )}
    </VStack>
  )

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" h="400px">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" thickness="4px" />
          <Text color="gray.500">분석 리포트를 생성 중...</Text>
        </VStack>
      </Box>
    )
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* 페이지 헤더 */}
      <Box>
        <HStack justify="space-between" wrap="wrap" gap={4}>
          <Box>
            <Heading size="lg" color="blue.700" mb={2}>
              📊 분석 리포트
            </Heading>
            <Text color="gray.600">
              기간별 질문 통계 분석 (전체 IP vs 외부 IP)
            </Text>
          </Box>
          
          {/* 기간 선택 */}
          <HStack spacing={4} bg={cardBg} p={4} borderRadius="lg" border="1px" borderColor={borderColor}>
            <Icon as={FiCalendar} color="blue.500" />
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              size="sm"
              w="150px"
            />
            <Text>~</Text>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              size="sm"
              w="150px"
            />
            <Button colorScheme="blue" size="sm" onClick={loadReport} isLoading={isLoading}>
              조회
            </Button>
          </HStack>
        </HStack>
      </Box>

      {error && (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
      )}

      {/* 데이터 범위 안내 */}
      {reportData && reportData.dataRange && (
        <Alert status={reportData.all.totalQueries === 0 ? "warning" : "info"} borderRadius="md">
          <AlertIcon />
          <Box>
            <Text fontWeight="bold">
              DB 데이터 범위: {formatDate(reportData.dataRange.minDate)} ~ {formatDate(reportData.dataRange.maxDate)} 
              (총 {reportData.dataRange.totalInDb.toLocaleString()}건)
            </Text>
            {reportData.all.totalQueries === 0 && (
              <Text fontSize="sm" mt={1}>
                ⚠️ 선택한 기간({startDate} ~ {endDate})에 데이터가 없습니다. 날짜 범위를 조정해주세요.
              </Text>
            )}
          </Box>
        </Alert>
      )}

      {reportData && (
        <>
          {/* DM 내부 IP 목록 (편집 가능) */}
          <Card bg={cardBg} border="1px" borderColor={borderColor}>
            <CardBody py={3}>
              <VStack align="stretch" spacing={3}>
                <HStack justify="space-between">
                  <HStack spacing={2}>
                    <Text fontSize="sm" fontWeight="bold" color="gray.600">DM 내부 IP (제외 대상):</Text>
                    <Badge colorScheme="blue">{parseExcludeIps().length}개</Badge>
                  </HStack>
                  <HStack spacing={2}>
                    <Tooltip label="기본값으로 초기화">
                      <IconButton
                        aria-label="기본값 초기화"
                        icon={<Icon as={FiRefreshCw} />}
                        size="sm"
                        variant="ghost"
                        onClick={resetToDefaultIps}
                      />
                    </Tooltip>
                    <Button
                      size="sm"
                      leftIcon={<Icon as={isIpEditorOpen ? FiX : FiEdit2} />}
                      variant={isIpEditorOpen ? "solid" : "outline"}
                      colorScheme={isIpEditorOpen ? "red" : "blue"}
                      onClick={onIpEditorToggle}
                    >
                      {isIpEditorOpen ? '닫기' : '편집'}
                    </Button>
                  </HStack>
                </HStack>
                
                {/* 현재 제외 IP 태그 표시 */}
                <Wrap spacing={2}>
                  {parseExcludeIps().map((ip, idx) => (
                    <WrapItem key={idx}>
                      <Tag size="sm" colorScheme="red" variant="subtle">{ip}</Tag>
                    </WrapItem>
                  ))}
                </Wrap>
                
                {/* IP 편집 영역 */}
                <Collapse in={isIpEditorOpen} animateOpacity>
                  <Box mt={2} p={3} bg="gray.50" borderRadius="md">
                    <VStack align="stretch" spacing={2}>
                      <Text fontSize="xs" color="gray.600">
                        제외할 IP 주소를 입력하세요. (줄바꿈, 쉼표, 공백으로 구분 가능)
                      </Text>
                      <Textarea
                        value={excludeIps}
                        onChange={(e) => setExcludeIps(e.target.value)}
                        placeholder="예: 127.0.0.1&#10;192.168.0.1&#10;10.0.0.1"
                        size="sm"
                        rows={6}
                        fontFamily="mono"
                        fontSize="sm"
                        bg="white"
                      />
                      <HStack justify="space-between">
                        <Text fontSize="xs" color="gray.500">
                          인식된 IP: {parseExcludeIps().length}개
                        </Text>
                        <Button
                          size="sm"
                          colorScheme="blue"
                          leftIcon={<Icon as={FiSave} />}
                          onClick={() => {
                            onIpEditorToggle()
                            loadReport()
                          }}
                        >
                          적용 및 조회
                        </Button>
                      </HStack>
                    </VStack>
                  </Box>
                </Collapse>
              </VStack>
            </CardBody>
          </Card>

          <Tabs colorScheme="blue" variant="enclosed-colored">
            <TabList>
              <Tab fontWeight="bold">
                <HStack>
                  <Icon as={FiUsers} />
                  <Text>전체 IP 통계</Text>
                </HStack>
              </Tab>
              <Tab fontWeight="bold">
                <HStack>
                  <Icon as={FiTrendingUp} />
                  <Text>외부 IP 통계 (DM 제외)</Text>
                </HStack>
              </Tab>
              <Tab fontWeight="bold">
                <HStack>
                  <Icon as={FiAlertCircle} />
                  <Text>외부 IP 에러 질문</Text>
                  {reportData.external.errorLogs.length > 0 && (
                    <Badge colorScheme="red" ml={1}>{reportData.external.errorLogs.length}</Badge>
                  )}
                </HStack>
              </Tab>
            </TabList>

            <TabPanels>
              {/* 전체 IP 통계 탭 */}
              <TabPanel px={0}>
                <VStack spacing={6} align="stretch">
                  {/* 주요 지표 카드 */}
                  <Grid templateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }} gap={4}>
                    <StatCard
                      label="전체 질문 수"
                      value={reportData.all.totalQueries.toLocaleString()}
                      subLabel={`${startDate} ~ ${endDate}`}
                      color="blue"
                      icon={<Icon as={FiHash} boxSize={5} />}
                    />
                    <StatCard
                      label="피크 데이"
                      value={formatDate(reportData.all.peakDay.date)}
                      subLabel={`${reportData.all.peakDay.count.toLocaleString()}건`}
                      color="orange"
                      icon={<Icon as={FiTrendingUp} boxSize={5} />}
                    />
                    <StatCard
                      label="답변 성공률"
                      value={`${reportData.all.answerRate}%`}
                      subLabel="SUCCESS 비율"
                      color="green"
                      icon={<Icon as={FiCheck} boxSize={5} />}
                    />
                    <StatCard
                      label="일평균 질문"
                      value={Math.round(reportData.all.totalQueries / 31).toLocaleString()}
                      subLabel="약 31일 기준"
                      color="purple"
                      icon={<Icon as={FiCalendar} boxSize={5} />}
                    />
                  </Grid>

                  <Grid templateColumns={{ base: '1fr', lg: '2fr 1fr' }} gap={6}>
                    {/* 일별 트렌드 차트 */}
                    <Card bg={cardBg} border="1px" borderColor={borderColor}>
                      <CardHeader>
                        <Heading size="md">📈 일별 질문 트렌드</Heading>
                      </CardHeader>
                      <CardBody>
                        <ResponsiveContainer width="100%" height={300}>
                          <LineChart data={reportData.all.dailyTrend}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="date" 
                              tickFormatter={(val) => formatDate(val)}
                              tick={{ fontSize: 10 }}
                              angle={-45}
                              textAnchor="end"
                              height={60}
                            />
                            <YAxis />
                            <ChartTooltip 
                              labelFormatter={(val) => formatDate(val)}
                              formatter={(value: number) => [`${value}건`, '질문 수']}
                            />
                            <Line 
                              type="monotone" 
                              dataKey="count" 
                              stroke="#3182ce" 
                              strokeWidth={2}
                              dot={{ fill: '#3182ce', r: 3 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </CardBody>
                    </Card>

                    {/* 키워드 Top 10 */}
                    <Card bg={cardBg} border="1px" borderColor={borderColor}>
                      <CardHeader>
                        <Heading size="md">🔑 키워드 Top 10</Heading>
                      </CardHeader>
                      <CardBody>
                        <KeywordRanking keywords={reportData.all.topKeywords} />
                      </CardBody>
                    </Card>
                  </Grid>
                </VStack>
              </TabPanel>

              {/* 외부 IP 통계 탭 */}
              <TabPanel px={0}>
                <VStack spacing={6} align="stretch">
                  {/* 주요 지표 카드 */}
                  <Grid templateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }} gap={4}>
                    <StatCard
                      label="외부 질문 수"
                      value={reportData.external.totalQueries.toLocaleString()}
                      subLabel="DM IP 제외"
                      color="teal"
                      icon={<Icon as={FiHash} boxSize={5} />}
                    />
                    <StatCard
                      label="피크 데이"
                      value={formatDate(reportData.external.peakDay.date)}
                      subLabel={`${reportData.external.peakDay.count.toLocaleString()}건`}
                      color="orange"
                      icon={<Icon as={FiTrendingUp} boxSize={5} />}
                    />
                    <StatCard
                      label="답변 성공률"
                      value={`${reportData.external.answerRate}%`}
                      subLabel="SUCCESS 비율"
                      color="green"
                      icon={<Icon as={FiCheck} boxSize={5} />}
                    />
                    <StatCard
                      label="에러 질문 수"
                      value={reportData.external.errorLogs.length.toLocaleString()}
                      subLabel="응답 실패"
                      color="red"
                      icon={<Icon as={FiX} boxSize={5} />}
                    />
                  </Grid>

                  <Grid templateColumns={{ base: '1fr', lg: '2fr 1fr' }} gap={6}>
                    {/* 일별 트렌드 비교 차트 */}
                    <Card bg={cardBg} border="1px" borderColor={borderColor}>
                      <CardHeader>
                        <Heading size="md">📈 일별 질문 트렌드 (외부 IP)</Heading>
                      </CardHeader>
                      <CardBody>
                        <ResponsiveContainer width="100%" height={300}>
                          <LineChart data={reportData.external.dailyTrend}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="date" 
                              tickFormatter={(val) => formatDate(val)}
                              tick={{ fontSize: 10 }}
                              angle={-45}
                              textAnchor="end"
                              height={60}
                            />
                            <YAxis />
                            <ChartTooltip 
                              labelFormatter={(val) => formatDate(val)}
                              formatter={(value: number) => [`${value}건`, '질문 수']}
                            />
                            <Line 
                              type="monotone" 
                              dataKey="count" 
                              stroke="#319795" 
                              strokeWidth={2}
                              dot={{ fill: '#319795', r: 3 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </CardBody>
                    </Card>

                    {/* 키워드 Top 10 */}
                    <Card bg={cardBg} border="1px" borderColor={borderColor}>
                      <CardHeader>
                        <Heading size="md">🔑 키워드 Top 10 (외부)</Heading>
                      </CardHeader>
                      <CardBody>
                        <KeywordRanking keywords={reportData.external.topKeywords} />
                      </CardBody>
                    </Card>
                  </Grid>

                  {/* 전체 vs 외부 비교 */}
                  <Card bg={cardBg} border="1px" borderColor={borderColor}>
                    <CardHeader>
                      <Heading size="md">📊 전체 vs 외부 비교</Heading>
                    </CardHeader>
                    <CardBody>
                      <Table variant="simple" size="sm">
                        <Thead>
                          <Tr>
                            <Th>구분</Th>
                            <Th isNumeric>전체 IP</Th>
                            <Th isNumeric>외부 IP (DM 제외)</Th>
                            <Th isNumeric>비율</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          <Tr>
                            <Td fontWeight="bold">총 질문 수</Td>
                            <Td isNumeric>{reportData.all.totalQueries.toLocaleString()}</Td>
                            <Td isNumeric>{reportData.external.totalQueries.toLocaleString()}</Td>
                            <Td isNumeric>
                              <Badge colorScheme="blue">
                                {reportData.all.totalQueries > 0 
                                  ? ((reportData.external.totalQueries / reportData.all.totalQueries) * 100).toFixed(1)
                                  : 0}%
                              </Badge>
                            </Td>
                          </Tr>
                          <Tr>
                            <Td fontWeight="bold">답변 성공률</Td>
                            <Td isNumeric>{reportData.all.answerRate}%</Td>
                            <Td isNumeric>{reportData.external.answerRate}%</Td>
                            <Td isNumeric>
                              <Badge colorScheme={reportData.external.answerRate >= reportData.all.answerRate ? 'green' : 'red'}>
                                {reportData.external.answerRate >= reportData.all.answerRate ? '+' : ''}
                                {(reportData.external.answerRate - reportData.all.answerRate).toFixed(1)}%p
                              </Badge>
                            </Td>
                          </Tr>
                          <Tr>
                            <Td fontWeight="bold">피크 데이</Td>
                            <Td isNumeric>{formatDate(reportData.all.peakDay.date)} ({reportData.all.peakDay.count}건)</Td>
                            <Td isNumeric>{formatDate(reportData.external.peakDay.date)} ({reportData.external.peakDay.count}건)</Td>
                            <Td isNumeric>-</Td>
                          </Tr>
                        </Tbody>
                      </Table>
                    </CardBody>
                  </Card>
                </VStack>
              </TabPanel>

              {/* 외부 IP 에러 질문 탭 */}
              <TabPanel px={0}>
                <Card bg={cardBg} border="1px" borderColor={borderColor}>
                  <CardHeader>
                    <HStack justify="space-between">
                      <Heading size="md">⚠️ 외부 IP 에러 질문 리스트</Heading>
                      <Badge colorScheme="red" fontSize="md" px={3} py={1}>
                        {reportData.external.errorLogs.length}건
                      </Badge>
                    </HStack>
                  </CardHeader>
                  <CardBody>
                    {reportData.external.errorLogs.length > 0 ? (
                      <Box overflowX="auto">
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              <Th w="60px">ID</Th>
                              <Th>질문</Th>
                              <Th w="150px">일시</Th>
                              <Th w="120px">IP</Th>
                              <Th w="100px">상태</Th>
                              <Th w="80px" isNumeric>신뢰도</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {reportData.external.errorLogs.map((log) => {
                              const isExpanded = expandedLogs.has(log.id)
                              return (
                                <React.Fragment key={log.id}>
                                  <Tr 
                                    _hover={{ bg: highlightBg }}
                                    cursor="pointer"
                                    onClick={() => {
                                      const newExpanded = new Set(expandedLogs)
                                      if (isExpanded) {
                                        newExpanded.delete(log.id)
                                      } else {
                                        newExpanded.add(log.id)
                                      }
                                      setExpandedLogs(newExpanded)
                                    }}
                                    bg={isExpanded ? 'blue.50' : undefined}
                                  >
                                    <Td fontSize="xs" color="gray.500">{log.id}</Td>
                                    <Td>
                                      <HStack spacing={2}>
                                        <Text fontSize="xs" color="blue.500">
                                          {isExpanded ? '▼' : '▶'}
                                        </Text>
                                        <Text 
                                          fontSize="sm" 
                                          noOfLines={isExpanded ? undefined : 2} 
                                          maxW="400px"
                                          fontWeight={isExpanded ? 'bold' : 'normal'}
                                        >
                                          {log.query}
                                        </Text>
                                      </HStack>
                                    </Td>
                                    <Td fontSize="xs" color="gray.600">
                                      {new Date(log.created_at).toLocaleString('ko-KR')}
                                    </Td>
                                    <Td>
                                      <Tag size="sm" colorScheme="gray">{log.ip}</Tag>
                                    </Td>
                                    <Td>
                                      <Badge colorScheme="red" variant="subtle">
                                        {log.status}
                                      </Badge>
                                    </Td>
                                    <Td isNumeric>
                                      <Text fontSize="sm" color={log.confidence > 0.5 ? 'green.500' : 'red.500'}>
                                        {(log.confidence * 100).toFixed(0)}%
                                      </Text>
                                    </Td>
                                  </Tr>
                                  {/* 확장된 답변 영역 */}
                                  {isExpanded && (
                                    <Tr>
                                      <Td colSpan={6} bg="gray.50" p={0}>
                                        <Box p={4} borderLeft="4px solid" borderColor="blue.400">
                                          <VStack align="stretch" spacing={2}>
                                            <HStack>
                                              <Badge colorScheme="blue">💬 답변</Badge>
                                            </HStack>
                                            <Box 
                                              bg="white" 
                                              p={3} 
                                              borderRadius="md" 
                                              border="1px" 
                                              borderColor="gray.200"
                                              maxH="300px"
                                              overflowY="auto"
                                            >
                                              {log.response ? (
                                                <Text 
                                                  fontSize="sm" 
                                                  whiteSpace="pre-wrap"
                                                  dangerouslySetInnerHTML={{ __html: log.response }}
                                                />
                                              ) : (
                                                <Text fontSize="sm" color="gray.400" fontStyle="italic">
                                                  (답변 없음)
                                                </Text>
                                              )}
                                            </Box>
                                          </VStack>
                                        </Box>
                                      </Td>
                                    </Tr>
                                  )}
                                </React.Fragment>
                              )
                            })}
                          </Tbody>
                        </Table>
                      </Box>
                    ) : (
                      <Box textAlign="center" py={10}>
                        <Icon as={FiCheck} boxSize={12} color="green.400" mb={4} />
                        <Text color="gray.500" fontSize="lg">에러 질문이 없습니다! 🎉</Text>
                        <Text color="gray.400" fontSize="sm">외부 사용자들의 모든 질문이 정상 처리되었습니다.</Text>
                      </Box>
                    )}
                  </CardBody>
                </Card>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </>
      )}
    </VStack>
  )
}

export default AnalysisReportPage
